/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
// @flow
import React, {useState} from 'react';
import gql from 'graphql-tag';
import classnames from 'classnames';
import {Link} from '@homeaway/react-localization-components';
import {
    AspectRatioCard,
    AspectRatioCardImage
} from '@homeaway/react-aspect-ratio-card';
import {TripBoardButton} from '@homeaway/react-trip-board-button';
import {FormattedMessage} from 'react-intl';
import PriceSummary from '@homeaway/react-price-summary';
import {Waypoint} from '@homeaway/react-unveil';
import ImagesCarousel from './ImagesCarousel/ImagesCarousel';
import ColorizedRating from '../../Rating/ColorizedRating/ColorizedRating';
import HitLabel from '../../HitLabel/HitLabel';

import type {PriceSummaryShape} from '../../Price/Price';
import type {Node} from 'react';

import SeeAllCard from './SeeAllCard/SeeAllCard';

import './PropertyCard.less';

export const fragments = {
    Listing: gql`
        fragment PropertyCardListing on Listing {
            averageRating
            bedrooms
            detailPageUrl
            headlinePhoto(treatment: C3) {
                uri
            }
            photos(treatment: C3) {
                uri
            }
            listingId
            priceSummary {
                formattedAmount
                pricePeriodDescription
            }
            propertyMetadata {
                headline
            }
            reviewCount
            sleeps
        }
    `
};

type PropertyCardImagesShape = {
    uri: string
};

type PropertyCardMetadataShape = {
    headline: string
};

export type PropertyCardListingShape = {
    averageRating: number,
    reviewCount: number,
    listingId: string,
    detailPageUrl: string,
    bedrooms: number,
    sleeps: number,
    headlinePhoto: PropertyCardImagesShape,
    photos: PropertyCardImagesShape[],
    priceSummary: PriceSummaryShape,
    propertyMetadata: PropertyCardMetadataShape,
    starSize: string,
    seeAllCard?: boolean
};

type Props = {
    listing: PropertyCardListingShape,
    aspectRatio: '16x9' | '3x2' | '4x3' | '1x1' | '3x4',
    isMobile: boolean,
    seeMoreUrl?: string,
    withTripboardButton: boolean,
    withImagesCarousel: boolean,
    labelGenerator?: Function,
    onCardClick: Function,
    onCardEnter: Function,
    edapCollectionType: string,
    locale?: string
};

const PropertyCard = (props: Props) => {
    const [waypointEntered, setWaypointEntered] = useState(false);
    const {
        listing,
        aspectRatio,
        isMobile,
        seeMoreUrl,
        withTripboardButton,
        withImagesCarousel,
        labelGenerator,
        onCardClick,
        onCardEnter,
        edapCollectionType,
        locale
    } = props;
    const {
        detailPageUrl,
        listingId,
        averageRating,
        reviewCount,
        headlinePhoto,
        photos,
        bedrooms,
        sleeps,
        priceSummary,
        propertyMetadata,
        seeAllCard
    } = listing;
    const hasBedroomsOrSleeps = bedrooms || sleeps;
    const showImagesCarousel = !isMobile && withImagesCarousel;

    listing.starSize = '12';

    const handleClick = () => {
        onCardClick(listingId);
    };

    const handleWaypointEnter = () => {
        if (!waypointEntered) {
            setWaypointEntered(true);
            onCardEnter(listingId);
        }
    };

    const className = classnames('PropertyCard', {
        'PropertyCard__font--small': withImagesCarousel,
        'PropertyCard--small': !withImagesCarousel
    });

    if (seeAllCard) {
        return <SeeAllCard aspectRatio={aspectRatio} seeMoreUrl={seeMoreUrl} />;
    }

    return (
        <Waypoint onEnter={handleWaypointEnter}>
            <div className={className}>
                {labelGenerator && (
                    <div className="PropertyCard__badge">
                        <HitLabel label={labelGenerator(listing, locale)} />
                    </div>
                )}
                {withTripboardButton && (
                    <div className="PropertyCard__tripboardButton">
                        <TripBoardButton
                            expandedButton={false}
                            listingId={listingId}
                            averageRating={averageRating}
                            reviewCount={reviewCount}
                            thumbnailUrl={headlinePhoto && headlinePhoto.uri}
                            useNativeEventListener={isMobile}
                            redOnHover
                            actionlocation={'search'}
                        />
                    </div>
                )}
                {showImagesCarousel && (
                    <ImagesCarousel
                        detailPageUrl={detailPageUrl}
                        handleClick={handleClick}
                        images={photos}
                        edapCollectionType={edapCollectionType}
                    />
                )}
                <div className="PropertyCard__info" onClick={handleClick}>
                    <Link
                        className="PropertyCard__anchor"
                        href={detailPageUrl}
                        target="_blank"
                    >
                        <AspectRatioCard
                            aspectRatio={aspectRatio}
                            hasHover={false}
                        >
                            {!showImagesCarousel && (
                                <AspectRatioCardImage
                                    data-slot="figure"
                                    alt={
                                        propertyMetadata &&
                                        propertyMetadata.headline
                                    }
                                    imageUri={
                                        headlinePhoto && headlinePhoto.uri
                                    }
                                />
                            )}
                            <div
                                className="PropertyCard__content"
                                data-slot="content"
                            >
                                <div className="PropertyCard__starRating">
                                    <ColorizedRating listing={listing} />
                                </div>
                                {!!hasBedroomsOrSleeps && (
                                    <div className="PropertyCard__details">
                                        {!!sleeps && (
                                            <span>
                                                <FormattedMessage
                                                    id="sleepsAbbreviation"
                                                    description="sleeps label"
                                                    defaultMessage="{value, plural, zero {Sleeps {value}} one {Sleeps {value}} few {Sleeps {value}} many {Sleeps {value}} other {Sleeps {value}}}"
                                                    values={{
                                                        value: sleeps
                                                    }}
                                                >
                                                    {(txt: Node): Node => txt}
                                                </FormattedMessage>
                                            </span>
                                        )}
                                        {!!bedrooms && (
                                            <span>
                                                <FormattedMessage
                                                    id="sectionTitleBedrooms"
                                                    description="bedrooms label"
                                                    defaultMessage="bedrooms"
                                                    values={{
                                                        bedrooms
                                                    }}
                                                >
                                                    {(txt: Node): Node => txt}
                                                </FormattedMessage>
                                            </span>
                                        )}
                                    </div>
                                )}
                                {!!priceSummary && (
                                    <div className="PropertyCard__price">
                                        <PriceSummary
                                            priceSummary={priceSummary}
                                        />
                                    </div>
                                )}
                            </div>
                        </AspectRatioCard>
                    </Link>
                </div>
            </div>
        </Waypoint>
    );
};

PropertyCard.defaultProps = {
    listing: {},
    aspectRatio: '3x2',
    edapCollectionType: ''
};

export default PropertyCard;
